import React, { useState, useEffect } from 'react';
import {
  Box, Flex, Text, HStack, Link, Button, IconButton,
  VStack, useDisclosure,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.5); }
`;

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Videos', path: '/videos' },
  { label: 'Blog', path: '/articles' },
  { label: 'Deals', path: '/deals' },
  { label: 'Resources', path: '/docs' },
  { label: 'Newsletter', path: '/#subscribe' },
  { label: 'Contact', path: '/#contact' },
];

export default function Navbar() {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);


  const handleNav = (path) => {
    onClose();
    if (path.includes('#')) {
      const [page, hash] = path.split('#');
      if (page === '' || page === '/') {
        navigate('/');
        setTimeout(() => {
          document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } else {
      navigate(path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Scroll Progress Bar */}
      <ScrollProgressBar />

      {/* Navbar */}
      <Box
        as="nav"
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={200}
        h="68px"
        bg={scrolled ? 'rgba(7,7,10,0.97)' : 'rgba(7,7,10,0.9)'}
        backdropFilter="blur(20px)"
        borderBottom="1px solid rgba(0,195,255,0.1)"
        transition="background 0.3s"
        boxShadow={scrolled ? '0 4px 30px rgba(0,0,0,0.4)' : 'none'}
      >
        <Flex
          maxW="1400px"
          mx="auto"
          px="5%"
          h="100%"
          align="center"
          justify="space-between"
        >
          {/* Logo */}
          <Flex
            align="center"
            gap={2}
            cursor="pointer"
            onClick={() => handleNav('/')}
            fontFamily="'Fraunces', serif"
            fontWeight={900}
            fontSize="1.35rem"
          >
            <Box
              w="9px"
              h="9px"
              bg="#00c3ff"
              borderRadius="50%"
              animation={`${pulse} 2s ease-in-out infinite`}
              boxShadow="0 0 8px rgba(0,195,255,0.8)"
            />
            <Text>
              Tech<Text as="span" color="#00c3ff">Bheekaran</Text>
            </Text>
          </Flex>

          {/* Desktop Nav */}
          <HStack as="ul" gap={6} display={{ base: 'none', md: 'flex' }} listStyleType="none">
            {navLinks.map((link) => (
              <Box as="li" key={link.label}>
                <Text
                  as="span"
                  color={isActive(link.path) ? '#00c3ff' : '#7878a0'}
                  fontSize="0.88rem"
                  fontWeight={isActive(link.path) ? 600 : 500}
                  cursor="pointer"
                  transition="color 0.2s"
                  _hover={{ color: '#00c3ff' }}
                  onClick={() => handleNav(link.path)}
                  position="relative"
                  _after={isActive(link.path) ? {
                    content: '""',
                    position: 'absolute',
                    bottom: '-4px',
                    left: 0,
                    right: 0,
                    height: '2px',
                    bg: '#00c3ff',
                    borderRadius: '2px',
                  } : {}}
                >
                  {link.label}
                </Text>
              </Box>
            ))}
          </HStack>

          {/* Right side */}
          <Flex align="center" gap={3}>
            <Link
              href="https://www.youtube.com/@techbheekaran"
              target="_blank"
              display={{ base: 'none', md: 'inline-flex' }}
              bg="#ec1a1a"
              color="#ffffff"
              fontWeight={700}
              fontSize="0.82rem"
              px={4}
              py={2}
              borderRadius="6px"
              whiteSpace="nowrap"
              transition="all 0.2s"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 24px rgba(236,26,26,0.4)',
                textDecoration: 'none',
              }}
            >
              ▶ Subscribe
            </Link>
            <IconButton
              display={{ base: 'flex', md: 'none' }}
              icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
              variant="ghost"
              color="white"
              aria-label="Menu"
              onClick={onToggle}
              _hover={{ bg: 'transparent', color: '#00c3ff' }}
            />
          </Flex>

        </Flex>
      </Box>

      {/* Mobile Menu */}
      {isOpen && (
        <Box
          position="fixed"
          inset={0}
          zIndex={190}
          bg="rgba(7,7,10,0.98)"
          backdropFilter="blur(30px)"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={7}
        >
          <IconButton
            position="absolute"
            top={5}
            right={6}
            icon={<CloseIcon />}
            variant="ghost"
            color="white"
            fontSize="1.8rem"
            aria-label="Close"
            onClick={onClose}
            _hover={{ color: '#00c3ff', bg: 'transparent' }}
          />
          {navLinks.map((link) => (
            <Text
              key={link.label}
              fontFamily="'Fraunces', serif"
              fontSize="1.8rem"
              fontWeight={700}
              cursor="pointer"
              color={isActive(link.path) ? '#00c3ff' : 'white'}
              _hover={{ color: '#00c3ff' }}
              onClick={() => handleNav(link.path)}
            >
              {link.label}
            </Text>
          ))}
          <Link
            href="https://www.youtube.com/@techbheekaran"
            target="_blank"
            bg="#00c3ff"
            color="#000"
            fontWeight={700}
            fontSize="1rem"
            px={6}
            py={3}
            borderRadius="8px"
            mt={4}
            _hover={{ textDecoration: 'none', boxShadow: '0 8px 24px rgba(0,195,255,0.4)' }}
          >
            ▶ Subscribe on YouTube
          </Link>
        </Box>
      )}
    </>
  );
}

function ScrollProgressBar() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handler = () => {
      const p = (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100;
      setProgress(p);
    };
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      h="3px"
      bgGradient="linear(to-r, #0077ff, #00c3ff)"
      zIndex={300}
      style={{ width: `${progress}%` }}
      transition="width 0.1s"
    />
  );
}
